import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { ForcedTheme } from 'components/ForcedTheme';
import { TIMELINE_MOBILE_LEGEND_HEIGHT } from 'features/mapping/components/timeline/timeline.constants';

/**
 * Mobile legend positioning.
 * - `viewport` — fixed below the app header (production map)
 * - `contained` — absolute at the top of a positioned parent (Storybook phone frame)
 */
export type TimelineMobileLegendMode = 'viewport' | 'contained';

export type TimelineMobileLegendProps = {
  children: ReactNode;
  mode?: TimelineMobileLegendMode;
  /** Legend item count, used to size the strip. */
  itemCount?: number;
};

// For 3 items: fit all. For 4+: show 3.5 items (4th half off-screen).
const legendWidth = (itemCount: number, unit: 'vw' | '%'): string =>
  itemCount <= 3 ? `100${unit}` : `calc(100${unit} * ${itemCount} / 3.5)`;

const viewportSx = {
  position: 'fixed',
  top: {
    xs: 'calc(56px + env(safe-area-inset-top))',
    sm: 'calc(64px + env(safe-area-inset-top))',
  },
  left: 0,
  right: 0,
  zIndex: 'modal',
  overflowX: 'scroll',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

const containedSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1,
  overflowX: 'scroll',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

/**
 * Horizontally scrollable strip holding a timeline layer's legend on mobile.
 * In `viewport` mode touch events are stopped so scrolling the legend doesn't
 * pan the map underneath.
 */
export const TimelineMobileLegend = ({
  children,
  mode = 'viewport',
  itemCount = 0,
}: TimelineMobileLegendProps): JSX.Element => {
  const isViewport = mode === 'viewport';
  const width = legendWidth(itemCount, isViewport ? 'vw' : '%');

  return (
    <ForcedTheme mode="dark">
      <Box
        component="div"
        sx={isViewport ? viewportSx : containedSx}
        onTouchStart={isViewport ? (e) => e.stopPropagation() : undefined}
        onTouchMove={isViewport ? (e) => e.stopPropagation() : undefined}
      >
        <Box
          sx={{ width, minWidth: width, height: TIMELINE_MOBILE_LEGEND_HEIGHT }}
        >
          {children}
        </Box>
      </Box>
    </ForcedTheme>
  );
};
